import { createTheme } from "@mui/material/styles";
import COLORS from "./constants/color-constants";

// DEARhealth admin theme — minimal, light, professional.
//
// The brand pairs a petrol "teal" with an "orange" accent. Teal leads as the primary
// interaction colour (calm, trustworthy, and — unlike the orange — it clears WCAG AA
// contrast against white), while orange is reserved as a secondary accent/highlight.
// A soft neutral canvas, hairline borders and a single restrained shadow keep the UI
// flat and uncluttered.

const FONT_STACK = [
  '"Inter"',
  "-apple-system",
  "BlinkMacSystemFont",
  '"Segoe UI"',
  "Roboto",
  '"Helvetica Neue"',
  "Arial",
  "sans-serif",
].join(",");

// One subtle elevation, used sparingly for cards/menus that need to lift off the canvas.
export const SOFT_SHADOW =
  "0 1px 2px rgba(16,24,40,0.04), 0 4px 16px rgba(16,24,40,0.06)";

const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: COLORS.teal,
      dark: COLORS.tealDark,
      light: COLORS.tealLight,
      contrastText: COLORS.white,
    },
    secondary: {
      main: COLORS.orangepeel,
      dark: COLORS.orangeDark,
      light: COLORS.orangeLight,
      // #ff9803 is a light orange: white text on it is only ~2.2:1 (fails WCAG). Dark ink on
      // orange is ~8.7:1, so secondary fills carry ink text and orange stays a bold accent.
      contrastText: COLORS.ink,
    },
    error: { main: COLORS.carmine },
    background: {
      default: COLORS.canvas,
      paper: COLORS.surface,
    },
    text: {
      primary: COLORS.ink,
      secondary: COLORS.inkMuted,
    },
    divider: COLORS.border,
  },
  shape: { borderRadius: 10 },
  typography: {
    fontFamily: FONT_STACK,
    h4: { fontWeight: 700, letterSpacing: "-0.02em" },
    h5: { fontWeight: 700, letterSpacing: "-0.015em" },
    h6: { fontWeight: 700, letterSpacing: "-0.01em" },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600 },
    button: { textTransform: "none", fontWeight: 600, letterSpacing: 0 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          WebkitFontSmoothing: "antialiased",
          MozOsxFontSmoothing: "grayscale",
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 8, paddingInline: 18 },
        sizeLarge: { paddingBlock: 12, fontSize: "1rem" },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: { textTransform: "none", fontWeight: 600 },
      },
    },
    MuiPaper: {
      styleOverrides: { root: { backgroundImage: "none" } },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          border: `1px solid ${COLORS.border}`,
          borderRadius: 14,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: { root: { borderRadius: 8 } },
    },
    MuiAppBar: {
      styleOverrides: {
        colorDefault: {
          backgroundColor: COLORS.surface,
          color: COLORS.ink,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: { textTransform: "none", fontWeight: 600, minWidth: 0 },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600 },
        sizeSmall: { height: 22 },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { fontSize: "0.75rem", borderRadius: 6 },
      },
    },
  },
});

export default theme;
